import { useState } from "react";
import { dropshippingProducts } from "@/mocks/dropshipping";
import DropshippingAuthModal from "./DropshippingAuthModal";
import SupplierDashboard from "./SupplierDashboard";
import SellerDashboard from "./SellerDashboard";
import BuyerOrderModal from "./BuyerOrderModal";
import DropshippingInfluencers from "./DropshippingInfluencers";

type Role = "buyer" | "seller" | "supplier";

interface AuthUser {
  role: Role;
  name: string;
}

const categories = ["All", "Electronics", "Kitchen", "Photography", "Wearables", "Furniture", "Sports", "Office", "Fitness", "Home"];

export default function DropshippingSection() {
  const [cart, setCart] = useState<number[]>([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [visibleCount, setVisibleCount] = useState(6);

  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authDefaultRole, setAuthDefaultRole] = useState<Role>("buyer");

  const [showSupplierDash, setShowSupplierDash] = useState(false);
  const [showSellerDash, setShowSellerDash] = useState(false);

  const [orderProduct, setOrderProduct] = useState<typeof dropshippingProducts[0] | null>(null);

  const openAuth = (role: Role) => {
    setAuthDefaultRole(role);
    setShowAuthModal(true);
  };

  const handleAuthSuccess = (role: Role, name: string) => {
    setAuthUser({ role, name });
    setShowAuthModal(false);
    if (role === "supplier") {
      setTimeout(() => setShowSupplierDash(true), 300);
    } else if (role === "seller") {
      setTimeout(() => setShowSellerDash(true), 300);
    }
  };

  const handleBuyNow = (product: typeof dropshippingProducts[0]) => {
    if (!authUser) { openAuth("buyer"); return; }
    setOrderProduct(product);
  };

  const addToCart = (id: number) => {
    if (!authUser) { openAuth("buyer"); return; }
    setCart((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  const filtered = activeCategory === "All"
    ? dropshippingProducts
    : dropshippingProducts.filter((p) => p.category === activeCategory);

  const visible = filtered.slice(0, visibleCount);

  const openDashboard = () => {
    if (authUser?.role === "supplier") setShowSupplierDash(true);
    else if (authUser?.role === "seller") setShowSellerDash(true);
  };

  return (
    <section id="dropshipping" className="py-16 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <span className="text-amber-500 text-sm font-semibold uppercase tracking-wider">Wholesale</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-1">Warehouse Dropshipping</h2>
            <p className="text-gray-500 text-sm mt-1">Direct from warehouse to customer — high margins, fast shipping</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {authUser ? (
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 bg-stone-100 rounded-lg px-3 py-2">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-amber-500">
                    <i className="ri-user-line text-white text-xs"></i>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{authUser.name}</span>
                  <span className="text-xs text-stone-400 capitalize">({authUser.role})</span>
                </div>
                {(authUser.role === "supplier" || authUser.role === "seller") && (
                  <button
                    onClick={openDashboard}
                    className={`text-sm font-semibold px-4 py-2 rounded-lg text-white transition-colors cursor-pointer whitespace-nowrap ${
                      authUser.role === "seller"
                        ? "bg-violet-500 hover:bg-violet-600"
                        : "bg-emerald-500 hover:bg-emerald-600"
                    }`}
                  >
                    <i className="ri-dashboard-line mr-1.5"></i>
                    My Dashboard
                  </button>
                )}
                <button
                  onClick={() => setAuthUser(null)}
                  className="text-sm text-stone-400 hover:text-stone-600 cursor-pointer whitespace-nowrap"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => openAuth("buyer")}
                  className="text-sm font-semibold px-4 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-shopping-bag-3-line mr-1.5"></i>
                  Buyer Sign Up
                </button>
                <button
                  onClick={() => openAuth("supplier")}
                  className="text-sm font-semibold px-4 py-2 rounded-lg border border-emerald-400 text-emerald-600 hover:bg-emerald-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-building-2-line mr-1.5"></i>
                  Supplier / Seller
                </button>
              </>
            )}
            {cart.length > 0 && (
              <div className="flex items-center gap-2 bg-amber-500 text-white px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer">
                <i className="ri-shopping-cart-line"></i>
                <span>{cart.length} items</span>
              </div>
            )}
          </div>
        </div>

        {/* Role Info Banner */}
        {!authUser && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            {[
              {
                role: "buyer" as Role,
                icon: "ri-shopping-bag-3-line",
                color: "text-amber-500",
                bg: "bg-amber-50 border-amber-100",
                title: "Buyers",
                desc: "Sign up to browse thousands of products and place orders with fast global shipping.",
                cta: "Sign Up as Buyer",
              },
              {
                role: "seller" as Role,
                icon: "ri-store-2-line",
                color: "text-violet-500",
                bg: "bg-violet-50 border-violet-100",
                title: "Sellers",
                desc: "Browse supplier products, set your own price, list them in your storefront and earn the margin.",
                cta: "Become a Seller",
              },
              {
                role: "supplier" as Role,
                icon: "ri-building-2-line",
                color: "text-emerald-500",
                bg: "bg-emerald-50 border-emerald-100",
                title: "Suppliers",
                desc: "Upload your products, set commission rates, and let influencers & sellers do the selling.",
                cta: "Join as Supplier",
              },
            ].map((item) => (
              <div key={item.role} className={`rounded-xl p-4 border ${item.bg} flex flex-col gap-2`}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className={`${item.icon} ${item.color} text-xl`}></i>
                  </div>
                  <span className="font-bold text-gray-900 text-sm">{item.title}</span>
                </div>
                <p className="text-stone-500 text-xs">{item.desc}</p>
                <button
                  onClick={() => openAuth(item.role)}
                  className={`text-xs font-semibold ${item.color} hover:underline cursor-pointer whitespace-nowrap text-left`}
                >
                  {item.cta} &rarr;
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => { setActiveCategory(cat); setVisibleCount(6); }}
              className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? "bg-amber-500 text-white"
                  : "bg-white border border-stone-200 text-stone-600 hover:border-amber-300 hover:text-amber-600"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6" data-product-shop>
          {visible.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-xl overflow-hidden border border-stone-100 hover:border-amber-200 transition-all duration-200 group"
            >
              <div className="relative overflow-hidden">
                <div className="w-full h-40 flex items-center justify-center overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="absolute top-2 left-2 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {product.margin}
                </div>
                <div className="absolute top-2 right-2 bg-amber-500/90 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {Math.round(product.commission * 100)}% comm.
                </div>
              </div>
              <div className="p-3">
                <span className="text-xs text-amber-600 font-medium">{product.category}</span>
                <h3 className="font-semibold text-gray-900 text-xs mt-0.5 mb-2 line-clamp-2">{product.name}</h3>
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-stone-400 text-xs line-through">{product.originalPrice}</span>
                  <span className="text-green-600 font-bold text-sm">{product.salePrice}</span>
                </div>
                <div className="mb-2">
                  <div className="flex justify-between text-xs text-stone-400 mb-1">
                    <span>Stock</span>
                    <span>{product.stock}</span>
                  </div>
                  <div className="bg-stone-100 rounded-full h-1">
                    <div
                      className="bg-amber-400 h-1 rounded-full"
                      style={{ width: `${Math.min((product.stock / product.maxStock) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
                <div className="text-xs text-stone-400 mb-2 flex items-center gap-1">
                  <i className="ri-truck-line"></i>{product.shipping}
                </div>
                <div className="text-xs text-stone-400 mb-3 flex items-center gap-1">
                  <i className="ri-shopping-cart-2-line"></i>{product.sold.toLocaleString()} sold
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => addToCart(product.id)}
                    className={`flex-1 text-xs font-semibold py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                      cart.includes(product.id)
                        ? "bg-green-500 text-white"
                        : "bg-stone-100 hover:bg-stone-200 text-stone-700"
                    }`}
                  >
                    {cart.includes(product.id) ? <i className="ri-check-line"></i> : <i className="ri-shopping-cart-line"></i>}
                  </button>
                  <button
                    onClick={() => handleBuyNow(product)}
                    className="flex-1 text-xs font-semibold py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Buy Now
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load More */}
        {visibleCount < filtered.length && (
          <div className="text-center mb-8">
            <button
              onClick={() => setVisibleCount((v) => v + 6)}
              className="inline-flex items-center gap-2 border border-amber-400 text-amber-600 hover:bg-amber-500 hover:text-white font-semibold px-8 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-add-line"></i>
              Load More Products ({filtered.length - visibleCount} more)
            </button>
          </div>
        )}

        {/* Influencer Commission Board */}
        <DropshippingInfluencers />
      </div>

      {/* Modals */}
      {showAuthModal && (
        <DropshippingAuthModal
          defaultRole={authDefaultRole}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      )}

      {showSupplierDash && authUser && (
        <SupplierDashboard
          supplierName={authUser.name}
          onClose={() => setShowSupplierDash(false)}
        />
      )}

      {showSellerDash && authUser && (
        <SellerDashboard
          sellerName={authUser.name}
          onClose={() => setShowSellerDash(false)}
        />
      )}

      {orderProduct && authUser && (
        <BuyerOrderModal
          product={orderProduct}
          buyerName={authUser.name}
          onClose={() => setOrderProduct(null)}
        />
      )}
    </section>
  );
}
